import React, { useEffect, useState } from 'react';
import { Route as RouteIcon, MapPin, Package, RefreshCw, Loader2, Navigation, Bike, ScanLine, Camera, CheckCircle2, QrCode, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { driverApi } from '../services/api';
import { toast } from 'sonner';
import QrPickupScanner from './QrPickupScanner';
import PodPhotoUploader from './PodPhotoUploader';

/**
 * DriverRoutePage — every remaining stop for the driver, ordered by shortest
 * driving distance from where they are. Each stop exposes the actions that
 * matter at that stage:
 *  - Pickup stops: Navigate (opens Google Maps) + Scan QR (opens phone camera)
 *  - Drop stops: Navigate + Take 2 POD photos + Delivered
 * Reuses QrPickupScanner + PodPhotoUploader components so the flow behaves
 * identically to /driver/deliveries.
 */
const DriverRoutePage = () => {
  const [state, setState] = useState({ loading: true, data: null });
  const [scanFor, setScanFor] = useState(null); // order_id
  const [manualScan, setManualScan] = useState(false); // driver-side "any QR" scanner
  const [showQr, setShowQr] = useState(null);  // { orderId, dataUrl, loading }
  const [podFor, setPodFor] = useState(null);   // { orderId, photos }
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await driverApi.optimizeRoute();
      setState({ loading: false, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build route');
      setState({ loading: false, data: null });
    }
  };
  useEffect(() => { load(); }, []);

  const stops = state.data?.stops || [];

  const markDelivered = async (orderId) => {
    setBusy(orderId);
    try {
      await driverApi.updateOrderStatus(orderId, 'delivered');
      toast.success('Order marked delivered');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not mark delivered'); }
    finally { setBusy(null); }
  };

  const navUrl = (s) => `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}&travelmode=driving`;

  const openShowQr = async (orderId) => {
    setShowQr({ orderId, loading: true });
    try {
      const data = await driverApi.orderQr(orderId);
      setShowQr({ orderId, dataUrl: data.data_url, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load QR');
      setShowQr(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-2xl mx-auto p-4 pt-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <RouteIcon className="h-6 w-6 text-emerald-600" /> My route
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Every stop, ordered by shortest driving distance from where you are.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={state.loading} data-testid="route-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${state.loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button size="sm" onClick={() => setManualScan(true)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="route-scan-qr-manual">
              <ScanLine className="h-4 w-4 mr-1.5" /> Scan QR manually
            </Button>
          </div>
        </div>

        {state.loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : stops.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="route-empty">
            <Bike className="h-8 w-8 text-slate-400 mx-auto" />
            <h3 className="mt-2 text-slate-900 font-semibold">No active stops</h3>
            <p className="text-sm text-slate-500 mt-1">Claim orders from the available feed and they&rsquo;ll show up here.</p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-3" data-testid="route-summary">
              <div className="flex flex-wrap items-center gap-3">
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total distance</div><div className="text-xl font-semibold text-slate-900 tabular-nums">{(state.data.total_km ?? 0).toFixed(1)} km</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Stops</div><div className="text-xl font-semibold text-slate-900 tabular-nums">{stops.length}</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Est. time</div><div className="text-xl font-semibold text-slate-900 tabular-nums">~{state.data.total_min ?? 0} min</div></div>
              </div>
              {state.data.directions_url && (
                <Button onClick={() => window.open(state.data.directions_url, '_blank', 'noopener')}
                  className="mt-3 bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto" data-testid="route-navigate-all">
                  <Navigation className="h-4 w-4 mr-1.5" /> Open all stops in Google Maps
                </Button>
              )}
            </div>

            <ol className="space-y-3" data-testid="route-stops">
              {stops.map((s, i) => {
                const isPickup = s.kind === 'pickup';
                const podCount = s.pod_photos?.length || 0;
                const canDeliver = ['picked_up', 'on_the_way', 'arrived_at_customer'].includes(s.status);
                return (
                  <li key={`${s.order_id}-${s.kind}`} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`route-stop-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-none text-white text-sm font-semibold ${isPickup ? 'bg-amber-500' : 'bg-emerald-600'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={`border-0 uppercase text-[10px] font-semibold tracking-wider ${isPickup ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {isPickup ? <><Package className="h-3 w-3 mr-1 inline" />Pickup</> : <><MapPin className="h-3 w-3 mr-1 inline" />Drop</>}
                          </Badge>
                          <span className="text-xs font-mono text-slate-500">{s.order_id}</span>
                        </div>
                        <div className="text-sm font-semibold text-slate-900 mt-1">{s.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{s.address}</div>
                        <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                          <span>{s.leg_km?.toFixed(1)} km leg</span><span>·</span>
                          <span>~{s.eta_min_from_start} min from start</span>
                        </div>
                      </div>
                    </div>

                    {/* Per-stop action bar */}
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        className="border-slate-300"
                        onClick={() => window.open(navUrl(s), '_blank', 'noopener')}
                        data-testid={`route-stop-navigate-${i}`}
                      >
                        <Navigation className="h-4 w-4 mr-1.5 text-slate-700" /> Navigate
                      </Button>

                      {isPickup ? (
                        <Button
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => setScanFor(s.order_id)}
                          data-testid={`route-stop-scan-${i}`}
                        >
                          <ScanLine className="h-4 w-4 mr-1.5" /> Scan QR to pick up
                        </Button>
                      ) : (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setPodFor({ orderId: s.order_id, photos: s.pod_photos || [] })}
                          disabled={!canDeliver}
                          data-testid={`route-stop-pod-${i}`}
                          title={!canDeliver ? 'Scan the pickup QR first' : ''}
                        >
                          <Camera className="h-4 w-4 mr-1.5" /> Take 2 delivery photos ({podCount}/2)
                        </Button>
                      )}
                    </div>
                    {isPickup && (
                      <Button
                        variant="outline"
                        className="mt-2 w-full border-slate-300 text-slate-700"
                        onClick={() => openShowQr(s.order_id)}
                        data-testid={`route-stop-show-qr-${i}`}
                      >
                        <QrCode className="h-4 w-4 mr-1.5" /> Show my QR for the vendor
                      </Button>
                    )}

                    {!isPickup && (
                      <Button
                        className="mt-2 w-full bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                        onClick={() => markDelivered(s.order_id)}
                        disabled={!canDeliver || busy === s.order_id}
                        title={!canDeliver ? 'Scan the pickup QR first' : ''}
                        data-testid={`route-stop-delivered-${i}`}
                      >
                        {busy === s.order_id
                          ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Marking…</>
                          : <><CheckCircle2 className="h-4 w-4 mr-1.5" />DELIVERED</>}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>

      <QrPickupScanner
        orderId={scanFor}
        open={scanFor != null}
        onClose={() => setScanFor(null)}
        onSuccess={() => { toast.success('Order picked up'); load(); }}
      />

      {/* Manual "any QR" scanner — backend infers which of the driver's orders the QR belongs to */}
      <QrPickupScanner
        orderId={null}
        open={manualScan}
        onClose={() => setManualScan(false)}
        onSuccess={() => { toast.success('Order picked up'); load(); }}
        verifyFn={async (_id, token) => {
          const r = await driverApi.scanQrAny(token);
          toast.success(`Picked up · ${r.vendor_name || r.order_id}`);
          return r;
        }}
        title="Scan QR manually"
        helpText="Point at any order QR — we'll match it to your active pickups automatically."
      />

      {/* Show-my-QR dialog for a specific pickup so the vendor can scan the driver's phone */}
      {showQr && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowQr(null)} data-testid="driver-show-qr-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <QrCode className="h-5 w-5 text-emerald-600" /> Your pickup QR
              </h3>
              <button onClick={() => setShowQr(null)} className="p-1 text-slate-500 hover:text-slate-800">
                <X className="h-4 w-4" />
              </button>
            </div>
            {showQr.loading ? (
              <div className="h-80 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : showQr.dataUrl ? (
              <div className="flex flex-col items-center gap-3">
                <img src={showQr.dataUrl} alt="Order QR" className="h-96 w-96 max-w-full border border-slate-200 rounded-lg" data-testid="driver-show-qr-image" />
                <p className="text-xs text-slate-500 text-center">Hold your phone up so the vendor can scan this to confirm the handover.</p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {podFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setPodFor(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-4" data-testid="route-pod-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-slate-900">Proof of delivery</h3>
              <button onClick={() => setPodFor(null)} className="text-slate-500 hover:text-slate-800 p-1">✕</button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Snap 2 photos — one showing the item at the door and one wide shot of the location.</p>
            <PodPhotoUploader
              orderId={podFor.orderId}
              value={podFor.photos}
              onChange={(fresh) => setPodFor((p) => ({ ...p, photos: fresh }))}
            />
            <Button
              className="mt-3 w-full bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => { setPodFor(null); load(); }}
              data-testid="route-pod-done"
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverRoutePage;
